import React, { useEffect, useState, useCallback } from 'react';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function fmtMoney(value, code = 'INR') {
  const n = Number(value || 0);
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency', currency: code, maximumFractionDigits: 0,
    }).format(n);
  } catch {
    return `${code} ${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
  }
}

function PmsRevenueTargetAdmin({ token, selectedProperty, permissions }) {
  const [year, setYear] = useState(new Date().getFullYear());
  const [targets, setTargets] = useState([]);
  const [draft, setDraft] = useState({}); // { month: { target_amount, notes } }
  const [loading, setLoading] = useState(false);
  const [savingMonth, setSavingMonth] = useState(null);
  const [error, setError] = useState('');
  const [audit, setAudit] = useState([]);
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditLoaded, setAuditLoaded] = useState(false);

  const currency = selectedProperty?.currency_code || 'INR';
  const canAdmin = Boolean(permissions?.revenue_target_admin || permissions?.hotel_admin);

  const authHeaders = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || '',
    'Content-Type': 'application/json',
  }), [token, selectedProperty]);

  const load = useCallback(async () => {
    if (!selectedProperty?.id) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`/api/v1/revenue-target/targets?year=${year}`, { headers: authHeaders() });
      const body = await res.json();
      if (!res.ok) throw new Error(body?.error || 'Failed to load');
      setTargets(body.targets || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedProperty, year, authHeaders]);

  useEffect(() => { load(); }, [load]);

  const targetByMonth = (m) => targets.find((t) => t.month === m);

  const startEdit = (m) => {
    const existing = targetByMonth(m);
    setDraft({
      ...draft,
      [m]: { target_amount: existing?.target_amount || '', notes: existing?.notes || '' },
    });
  };

  const cancelEdit = (m) => {
    const d = { ...draft };
    delete d[m];
    setDraft(d);
  };

  const save = async (m) => {
    if (!canAdmin) return;
    const d = draft[m];
    if (!d || d.target_amount === '' || Number(d.target_amount) < 0) {
      setError('Enter a non-negative amount');
      return;
    }
    setSavingMonth(m);
    setError('');
    try {
      const res = await fetch('/api/v1/revenue-target/targets', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ year, month: m, target_amount: Number(d.target_amount), notes: d.notes || null }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body?.error || 'Save failed');
      cancelEdit(m);
      load();
      if (auditOpen) loadAudit();
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingMonth(null);
    }
  };

  const unset = async (m) => {
    if (!canAdmin) return;
    const t = targetByMonth(m);
    if (!t) return;
    if (!window.confirm(`Unset target for ${MONTHS[m - 1]} ${year}?`)) return;
    setSavingMonth(m);
    try {
      const res = await fetch(`/api/v1/revenue-target/targets/${t.target_id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body?.error || 'Delete failed');
      load();
      if (auditOpen) loadAudit();
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingMonth(null);
    }
  };

  const loadAudit = useCallback(async () => {
    if (!selectedProperty?.id || !canAdmin) return;
    try {
      const res = await fetch('/api/v1/revenue-target/targets/audit', { headers: authHeaders() });
      const body = await res.json();
      if (res.ok) {
        setAudit(body.data || []);
        setAuditLoaded(true);
      }
    } catch {}
  }, [selectedProperty, authHeaders, canAdmin]);

  if (!selectedProperty?.id) {
    return <div style={{ padding: 20 }}><div className="error-banner">Please select a property first.</div></div>;
  }

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        <h4 style={{ margin: 0, color: 'var(--theme-primary)' }}>Monthly Revenue Targets</h4>
        <label style={{ fontSize: 12, fontWeight: 600 }}>Year</label>
        <select value={year} onChange={(e) => setYear(Number(e.target.value))}
          style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}>
          {[year - 2, year - 1, year, year + 1, year + 2].map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
        {loading && <span style={{ fontSize: 12, color: '#6b7280' }}>Loading…</span>}
        {!canAdmin && (
          <span style={{ fontSize: 12, color: '#92400e', background: '#fffbeb',
            padding: '4px 8px', borderRadius: 4 }}>
            Read-only — target admin permission required to edit
          </span>
        )}
      </div>

      {error && <div className="error-banner" style={{ marginBottom: 12 }}>{error}</div>}

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, background: '#fff', border: '1px solid #e5e7eb' }}>
        <thead>
          <tr style={{ background: '#f8fafc' }}>
            <th style={{ padding: '8px 10px', textAlign: 'left' }}>Month</th>
            <th style={{ padding: '8px 10px', textAlign: 'right' }}>Target Amount</th>
            <th style={{ padding: '8px 10px', textAlign: 'left' }}>Notes</th>
            <th style={{ padding: '8px 10px', textAlign: 'left' }}>Updated</th>
            <th style={{ padding: '8px 10px', textAlign: 'right' }}>Action</th>
          </tr>
        </thead>
        <tbody>
          {MONTHS.map((label, idx) => {
            const m = idx + 1;
            const t = targetByMonth(m);
            const d = draft[m];
            const editing = !!d;
            return (
              <tr key={m} style={{ borderTop: '1px solid #f1f5f9' }}>
                <td style={{ padding: '8px 10px' }}>{label}</td>
                <td style={{ padding: '8px 10px', textAlign: 'right', fontFamily: 'monospace' }}>
                  {editing ? (
                    <input type="number" min="0" step="100"
                      value={d.target_amount}
                      onChange={(e) => setDraft({ ...draft, [m]: { ...d, target_amount: e.target.value } })}
                      style={{ width: 140, padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: 4, textAlign: 'right' }} />
                  ) : (
                    t ? fmtMoney(t.target_amount, currency) : <span style={{ color: '#9ca3af' }}>—</span>
                  )}
                </td>
                <td style={{ padding: '8px 10px' }}>
                  {editing ? (
                    <input type="text" value={d.notes}
                      onChange={(e) => setDraft({ ...draft, [m]: { ...d, notes: e.target.value } })}
                      placeholder="Optional"
                      style={{ width: '100%', padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: 4 }} />
                  ) : (
                    t?.notes || <span style={{ color: '#9ca3af' }}>—</span>
                  )}
                </td>
                <td style={{ padding: '8px 10px', color: '#6b7280', fontSize: 12 }}>
                  {t?.updated_at ? new Date(t.updated_at).toLocaleDateString() : '—'}
                </td>
                <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                  {!canAdmin ? null
                    : editing ? (
                      <>
                        <button onClick={() => save(m)} disabled={savingMonth === m}
                          style={{ padding: '4px 12px', background: '#16a34a', color: '#fff',
                            border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, marginRight: 6 }}>
                          {savingMonth === m ? '…' : 'Save'}
                        </button>
                        <button onClick={() => cancelEdit(m)}
                          style={{ padding: '4px 10px', background: '#fff', color: '#475569',
                            border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => startEdit(m)}
                          style={{ padding: '4px 12px', background: '#2563eb', color: '#fff',
                            border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, marginRight: 6 }}>
                          {t ? 'Edit' : 'Set'}
                        </button>
                        {t && (
                          <button onClick={() => unset(m)} disabled={savingMonth === m}
                            style={{ padding: '4px 10px', background: '#fff', color: '#b91c1c',
                              border: '1px solid #fca5a5', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>
                            Unset
                          </button>
                        )}
                      </>
                    )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {canAdmin && (
        <div style={{ marginTop: 16 }}>
          <button onClick={() => { const next = !auditOpen; setAuditOpen(next); if (next && !auditLoaded) loadAudit(); }}
            style={{ padding: '6px 12px', background: '#fff', color: 'var(--theme-text)',
              border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>
            {auditOpen ? 'Hide audit log' : 'Show audit log'}
          </button>
          {auditOpen && (
            <div style={{ marginTop: 10, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 10 }}>
              {audit.length === 0 ? (
                <div style={{ fontSize: 12, color: '#6b7280' }}>No changes yet.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: '#f9fafb' }}>
                      <th style={{ padding: '5px 8px', textAlign: 'left' }}>When</th>
                      <th style={{ padding: '5px 8px', textAlign: 'left' }}>Who</th>
                      <th style={{ padding: '5px 8px', textAlign: 'left' }}>Action</th>
                      <th style={{ padding: '5px 8px', textAlign: 'left' }}>Period</th>
                      <th style={{ padding: '5px 8px', textAlign: 'right' }}>Old → New</th>
                    </tr>
                  </thead>
                  <tbody>
                    {audit.map((a) => (
                      <tr key={a.audit_id} style={{ borderTop: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '5px 8px' }}>{new Date(a.changed_at).toLocaleString()}</td>
                        <td style={{ padding: '5px 8px' }}>{a.actor_name || '—'}</td>
                        <td style={{ padding: '5px 8px' }}>{a.action}</td>
                        <td style={{ padding: '5px 8px' }}>{MONTHS[a.month - 1]} {a.year}</td>
                        <td style={{ padding: '5px 8px', textAlign: 'right', fontFamily: 'monospace' }}>
                          {a.old_amount != null ? fmtMoney(a.old_amount, currency) : '—'}
                          {' → '}
                          {a.new_amount != null ? fmtMoney(a.new_amount, currency) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default PmsRevenueTargetAdmin;
